import { useState, useCallback, useEffect, useRef } from 'react'
import { Link } from '@tanstack/react-router'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { resolveLanguage } from '@/application/i18n/language'
import { investigationSlug } from '@/application/seo/investigation-url'
import { Badge } from '@/ui/components/common/Badge'
import { ConclusionStamp } from '@/ui/components/public/ConclusionStamp'
import { classNames } from '@/ui/utils/main'
import { resolveSwipe } from '@/ui/components/public/carouselSwipe'
import type { Investigation } from '@/domains/types'

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

interface InvestigationsCarouselProps {
  investigations: Investigation[]
  className?: string
}

// ─────────────────────────────────────────────────────────────
// Animation variants — fade + subtle horizontal slide
// ─────────────────────────────────────────────────────────────

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 60 : -60,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 60 : -60,
    opacity: 0,
  }),
}

const slideTransition = { duration: 0.4, ease: 'easeInOut' }

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export function InvestigationsCarousel({ investigations, className }: InvestigationsCarouselProps) {
  const { t, i18n } = useTranslation()
  const locale = resolveLanguage(i18n.language)

  // [slideIndex, slideDirection]
  const [[current, direction], setPage] = useState<[number, number]>([0, 0])

  // ── Navigation helpers ──────────────────────────────────────

  const goTo = useCallback((index: number, direction: number) => {
    setPage([index, direction])
  }, [])

  const prev = useCallback(() => {
    setPage(([curr]) => [(curr - 1 + investigations.length) % investigations.length, -1])
  }, [investigations.length])

  const next = useCallback(() => {
    setPage(([curr]) => [(curr + 1) % investigations.length, 1])
  }, [investigations.length])

  // ── Auto-advance every 6 s; reset on manual navigation ──────

  const [isDragging, setIsDragging] = useState(false)

  useEffect(() => {
    if (investigations.length <= 1) return
    // Holding a slide pauses the rotation, so it never changes mid-gesture.
    if (isDragging) return
    const timer = setInterval(() => {
      setPage(([curr]) => [(curr + 1) % investigations.length, 1])
    }, 6_000)
    return () => clearInterval(timer)
  }, [investigations.length, current, isDragging]) // `current` dep intentionally resets the timer on manual nav

  // Distinguishes a swipe from a tap: the whole slide is a `<Link>`, so a
  // drag that ends up navigating would make the carousel unusable by touch.
  const isSwiping = useRef(false)

  const handleDragEnd = useCallback(
    (_event: unknown, info: { offset: { x: number }; velocity: { x: number } }) => {
      const outcome = resolveSwipe(info.offset.x, info.velocity.x)
      if (outcome === 'next') next()
      else if (outcome === 'prev') prev()

      // Released: let the click that follows this gesture through again. The
      // reset is deferred a tick so the synthetic click fired at the end of a
      // drag is still suppressed by the capture handler below.
      setIsDragging(false)
      window.setTimeout(() => {
        isSwiping.current = false
      }, 0)
    },
    [next, prev],
  )

  if (investigations.length === 0) return null

  const investigation = investigations[current]
  const canSwipe = investigations.length > 1

  return (
    <div
      className={classNames('relative w-full overflow-hidden rounded-xl', className)}
      style={{ height: '380px' }}
    >
      {/* ── Slides ──────────────────────────────────────────── */}
      <AnimatePresence initial={false} custom={direction} mode="wait">
        <motion.div
          key={current}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={slideTransition}
          drag={canSwipe ? 'x' : false}
          dragDirectionLock
          // Rubber-band at the ends rather than moving freely: the slide
          // follows the finger a little, then springs back if the gesture is
          // too small to page.
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.2}
          onDragStart={() => {
            isSwiping.current = true
            setIsDragging(true)
          }}
          onDragEnd={handleDragEnd}
          className="absolute inset-0 touch-pan-y"
          style={{
            backgroundImage: investigation.imageUrl
              ? `url(${investigation.imageUrl})`
              : // Fallback gradient (no imageUrl): uses the theme's own
                // primary/accent tokens so the *hue* still adapts per theme,
                // but is deliberately dark regardless of theme (fixed low
                // lightness via hsl(...) with an explicit dark mix) — the
                // scrim + white text below assume a dark-enough backdrop,
                // exactly like an arbitrary uploaded photo would be treated.
                'linear-gradient(135deg, hsl(var(--accent)) 0%, hsl(var(--primary)) 60%, hsl(var(--accent)) 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {/* Fixed dark scrim for text legibility. The background here is an
              arbitrary, unpredictable per-investigation photo (or the dark
              fallback gradient above) — never the page's own surface — so
              this must NOT be theme-token-driven (a light theme's
              `--foreground` is dark, which produced a washed-out, low-opacity
              scrim and dark-on-dark text; see the carousel legibility
              regression this replaced). It is intentionally always a literal
              black gradient, independent of the active theme. */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

          {/* Full-cover clickable link — all slide content lives here */}
          <Link
            to="/investigations/$slug"
            params={{ slug: investigationSlug(investigation, locale) }}
            // A drag ends with a synthetic click on the link under the finger.
            // Swallow it during a swipe so paging never navigates away.
            onClickCapture={(event) => {
              if (isSwiping.current) {
                event.preventDefault()
                event.stopPropagation()
              }
            }}
            // The image would otherwise start a native drag and eat the gesture.
            draggable={false}
            className="absolute inset-0 z-10 flex flex-col p-6 md:p-8 no-underline hover:no-underline focus-visible:outline-none"
          >
            {/* ── ENQUÊTE brand label — top-left ─────────────── */}
            <div className="flex-none flex items-start justify-between">
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-widest bg-primary/90 text-primary-foreground border border-primary/50 shadow-sm">
                {t('common.investigation')}
              </span>

              {/* Conclusion badge — top-right, dark variant for overlay */}
              {investigation.conclusion && (
                <ConclusionStamp
                  conclusion={investigation.conclusion}
                  size={110}
                  dark
                  opaqueBackground
                />
              )}
            </div>
            <div className="flex-1" />

            {/* ── Bottom-aligned content ─────────────────────── */}
            <div className="flex flex-col gap-2">
              {investigation.categoryTerms && investigation.categoryTerms.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {investigation.categoryTerms.map((term) => (
                    <Badge
                      key={term.id}
                      variant="outline"
                      className="border-white/30 text-white/90 bg-black/30"
                    >
                      {term.label}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Fixed white text over the fixed dark scrim above — NOT
                  text-foreground. The scrim's contrast guarantee only holds
                  if the text color is also fixed: a theme whose
                  `--foreground` is dark (light themes) would otherwise
                  render dark text on a dark scrim. See the scrim comment
                  above for the full rationale. */}
              <h3 className="font-serif font-bold text-white text-3xl md:text-4xl drop-shadow-lg leading-tight">
                {investigation.name}
              </h3>

              {investigation.author && (
                <p className="text-white/70 text-sm drop-shadow">
                  {t('investigation_detail.by')} {investigation.author}
                </p>
              )}
            </div>
          </Link>
        </motion.div>
      </AnimatePresence>

      {/* ── Prev / Next chevron buttons ──────────────────────── */}
      {investigations.length > 1 && (
        <>
          {/* Chevrons overlay an arbitrary photo (or the fallback gradient)
              directly, same as the scrim/text above — fixed dark pill +
              fixed white icon, independent of theme, so they stay legible
              regardless of what is rendered underneath. */}
          <button
            onClick={prev}
            aria-label={t('home.carousel_prev')}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-10 h-10 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <button
            onClick={next}
            aria-label={t('home.carousel_next')}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-10 h-10 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}

      {/* ── Dot indicators ───────────────────────────────────── */}
      {investigations.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5">
          {investigations.map((_item, index) => (
            <button
              key={index}
              onClick={() => goTo(index, index > current ? 1 : -1)}
              aria-label={t('common.slide', { number: index + 1 })}
              className={[
                'h-1.5 rounded-full transition-all duration-300',
                // Inactive dots overlay the image/gradient directly, same
                // legibility concern as the scrim/text/chevrons above — fixed
                // white, not theme-token-driven. The active dot sits on the
                // same backdrop but is small and filled solid, so --primary
                // (a designed contrast pair with --primary-foreground) reads
                // fine there; verify visually if a future theme's --primary
                // ever approaches near-black or near-white.
                index === current ? 'w-6 bg-primary' : 'w-1.5 bg-white/50 hover:bg-white/70',
              ].join(' ')}
            />
          ))}
        </div>
      )}
    </div>
  )
}
